import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, AlertCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackAuthAction } from "@/utils/analytics";
import { MFAVerifyDialog } from "@/components/MFAVerifyDialog";
import { generateDeviceFingerprint } from "@/utils/mfaHelpers";
import { z } from "zod";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";

// Strong password validation schema
const passwordSchema = z.string()
  .min(10, "Passwort muss mindestens 10 Zeichen lang sein")
  .regex(/[A-Z]/, "Passwort muss mindestens einen Großbuchstaben enthalten")
  .regex(/[a-z]/, "Passwort muss mindestens einen Kleinbuchstaben enthalten")
  .regex(/[0-9]/, "Passwort muss mindestens eine Ziffer enthalten")
  .regex(/[^A-Za-z0-9]/, "Passwort muss mindestens ein Sonderzeichen enthalten");

const Auth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string>("");
  const [checkingMFA, setCheckingMFA] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/");
      }
    };
    checkUser();
  }, [navigate]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate password strength
      const passwordValidation = passwordSchema.safeParse(password);
      if (!passwordValidation.success) {
        passwordValidation.error.errors.forEach(err => {
          toast.error(err.message);
        });
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Willkommen bei MediQR! Sie erhalten gleich eine Willkommens-E-Mail.");
        trackAuthAction('signup');
        
        // Send welcome email
        if (data.session) {
          try {
            await supabase.functions.invoke('send-welcome-email', {
              headers: {
                Authorization: `Bearer ${data.session.access_token}`
              }
            });
          } catch (emailError) {
            console.error('Failed to send welcome email:', emailError);
            // Don't show error to user, just log it
          }
        }

        // Notify admin about new signup
        try {
          await supabase.functions.invoke('send-admin-signup-notification', {
            body: { email, userId: data.user?.id },
          });
        } catch (notifyError) {
          console.error('Failed to send admin notification:', notifyError);
        }
        
        
        setEmail("");
        setPassword("");
      }
    } catch (error) {
      toast.error("Ein Fehler ist aufgetreten");
    } finally {
      setLoading(false);
    }
  };

  const checkMFAStatus = async (userId: string) => {
    try {
      // Check if MFA is enabled
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totpFactor = factors?.totp?.[0];
      
      if (!totpFactor) {
        return { required: false };
      }
      
      // Check for trusted devices
      const fingerprint = await generateDeviceFingerprint();
      const { data: trustedDevice } = await supabase
        .from('mfa_devices')
        .select('*')
        .eq('user_id', userId)
        .eq('device_fingerprint', fingerprint)
        .gt('trusted_until', new Date().toISOString())
        .maybeSingle();
      
      if (trustedDevice) {
        // Update last used timestamp
        await supabase
          .from('mfa_devices')
          .update({ last_used_at: new Date().toISOString() })
          .eq('id', trustedDevice.id);
        
        return { required: false };
      }
      
      return { 
        required: true, 
        factorId: totpFactor.id 
      };
    } catch (error) {
      console.error('Error checking MFA status:', error);
      return { required: false };
    }
  };

  const handleMFASuccess = () => {
    setMfaRequired(false);
    toast.success("Erfolgreich angemeldet!");
    trackAuthAction('login');
    navigate("/");
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      // Check MFA status
      setCheckingMFA(true);
      const mfaCheck = await checkMFAStatus(data.user.id);
      
      if (mfaCheck.required) {
        setMfaFactorId(mfaCheck.factorId);
        setMfaRequired(true);
        toast.info("Bitte verifizieren Sie Ihre Identität");
      } else {
        toast.success("Erfolgreich angemeldet!");
        trackAuthAction('login');
        navigate("/");
      }
    } catch (error) {
      toast.error("Ein Fehler ist aufgetreten");
    } finally {
      setLoading(false);
      setCheckingMFA(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle py-12 px-4">
      <div className="container mx-auto max-w-md">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück
          </Button>
        </Link>

        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold mb-2">
            <span className="gradient-text">MediQR</span>
          </h1>
          <p className="text-muted-foreground">
            Melden Sie sich an oder erstellen Sie ein Konto
          </p>
        </div>

        <Card className="p-6 glass">
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Anmelden</TabsTrigger>
              <TabsTrigger value="signup">Registrieren</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Label htmlFor="signin-email">E-Mail</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="ihre@email.de"
                  />
                </div>
                <div>
                  <Label htmlFor="signin-password">Passwort</Label>
                  <Input
                    id="signin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    minLength={6}
                  />
                 </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-primary hover:shadow-glow"
                  disabled={loading || checkingMFA}
                >
                  {checkingMFA ? "MFA wird geprüft..." : loading ? "Wird geladen..." : "Anmelden"}
                </Button>
                
                <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Passwort vergessen?</p>
                      <p className="text-xs text-muted-foreground">
                        Aus Sicherheitsgründen für Ihre sensiblen Gesundheitsdaten erfolgt die Passwort-Zurücksetzung 
                        ausschließlich über unseren Support. Dies gewährleistet maximale Sicherheit und verhindert 
                        unbefugten Zugriff auf Ihre medizinischen Informationen.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        type="button"
                        onClick={() => window.location.href = 'mailto:R.Dieker@DiekerIT.com?subject=MediQR - Passwort zurücksetzen&body=Hallo,%0D%0A%0D%0AIch möchte mein Passwort zurücksetzen.%0D%0A%0D%0AMeine registrierte E-Mail-Adresse: ' + email}
                      >
                        <Mail className="mr-2 h-3 w-3" />
                        Support kontaktieren
                      </Button>
                    </div>
                  </div>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div>
                  <Label htmlFor="signup-email">E-Mail</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="ihre@email.de"
                  />
                </div>
                <div>
                  <Label htmlFor="signup-password">Passwort</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    minLength={10}
                  />
                  <PasswordStrengthIndicator password={password} />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-primary hover:shadow-glow"
                  disabled={loading}
                >
                  {loading ? "Wird geladen..." : "Konto erstellen"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>

        <MFAVerifyDialog
          open={mfaRequired}
          onOpenChange={setMfaRequired}
          factorId={mfaFactorId}
          onSuccess={handleMFASuccess}
        />
      </div>
    </div>
  );
};

export default Auth;
